import * as React from 'react';

import { useIsTablet } from '@/hooks/use-tablet';

type FocusableField = HTMLInputElement | HTMLTextAreaElement;

/**
 * On touch tablets, ignore programmatic / navigation focus so the virtual
 * keyboard only opens after the user taps the field.
 */
export function useTabletKeyboardGuard<
  T extends FocusableField = HTMLInputElement,
>() {
  const isTablet = useIsTablet();
  const userIntentRef = React.useRef(false);

  const mergeFieldProps = React.useCallback(
    <P extends React.ComponentPropsWithoutRef<'input'> | React.ComponentPropsWithoutRef<'textarea'>>(
      props: P,
    ): P => {
      if (!isTablet) return props;

      const {
        onPointerDown,
        onClick,
        onFocus,
        autoFocus: _autoFocus,
        ...rest
      } = props as P & {
        onPointerDown?: React.PointerEventHandler<T>;
        onClick?: React.MouseEventHandler<T>;
        onFocus?: React.FocusEventHandler<T>;
        autoFocus?: boolean;
      };

      return {
        ...rest,
        autoFocus: false,
        onPointerDown: (e: React.PointerEvent<T>) => {
          userIntentRef.current = true;
          onPointerDown?.(e);
        },
        onClick: (e: React.MouseEvent<T>) => {
          userIntentRef.current = true;
          onClick?.(e);
        },
        onFocus: (e: React.FocusEvent<T>) => {
          if (!userIntentRef.current) {
            e.currentTarget.blur();
          }
          userIntentRef.current = false;
          onFocus?.(e);
        },
      } as P;
    },
    [isTablet],
  );

  return { isTablet, mergeFieldProps };
}

export function isFocusableFormField(
  el: Element | null,
): el is HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement {
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement
  );
}
